import type { Homework } from '@mathflat/domain/Homework'
import type { StudentWorksheet } from '@mathflat/domain/StudentWorksheet'
import type { SelfLearningWorksheet, Worksheet } from '@mathflat/domain/Worksheet'

import { SelfLearning, SelfLearningColl } from './SelfLearning.dto'

export class AssignedStudentWorksheetHomeworksSelfLearnings {
  protected _studentWorksheet: StudentWorksheet
  protected _worksheet: Worksheet
  private _group: {
    studentWorksheet: StudentWorksheet
    worksheet: SelfLearningWorksheet
  }[] = []
  private _selfLearningColl: SelfLearningColl
  private _originAssignedStudentWorksheet: AssignedStudentWorksheetHomeworksSelfLearnings | null
  homeworks: Homework[] = []

  constructor(assignedStudentWorksheetWithHomeworks: {
    worksheet: Worksheet
    selfLearningStudentWorksheets?: {
      studentWorksheet: StudentWorksheet
      worksheet: SelfLearningWorksheet
      group: {
        studentWorksheet: StudentWorksheet
        worksheet: SelfLearningWorksheet
      }[]
    }[]
    homeworks?: Homework[]
    studentWorksheet: StudentWorksheet
    group?: {
      studentWorksheet: StudentWorksheet
      worksheet: SelfLearningWorksheet
    }[]
    originAssignedStudentWorksheet?: AssignedStudentWorksheetHomeworksSelfLearnings
  }) {
    this._studentWorksheet = assignedStudentWorksheetWithHomeworks.studentWorksheet
    this._worksheet = assignedStudentWorksheetWithHomeworks.worksheet
    this.homeworks = assignedStudentWorksheetWithHomeworks.homeworks ?? []
    this._selfLearningColl = new SelfLearningColl(
      (assignedStudentWorksheetWithHomeworks.selfLearningStudentWorksheets ?? []).map(
        (selfLearning) => new SelfLearning(selfLearning),
      ),
    )
    this._group = assignedStudentWorksheetWithHomeworks.group ?? []
    this._originAssignedStudentWorksheet =
      assignedStudentWorksheetWithHomeworks.originAssignedStudentWorksheet ?? null
  }

  get worksheet() {
    return this._worksheet
  }
  get studentWorksheet() {
    return this._studentWorksheet
  }

  get group() {
    return this._group
  }

  get correctCount() {
    if (this.group.length > 1) {
      return this.group.reduce((result, curr) => {
        return curr.studentWorksheet.correctCount
          ? result + curr.studentWorksheet.correctCount
          : result
      }, 0)
    }

    return this._studentWorksheet.correctCount
  }

  get wrongCount() {
    if (this.group.length > 1) {
      return this.group.reduce((result, curr) => {
        return curr.studentWorksheet.wrongCount ? result + curr.studentWorksheet.wrongCount : result
      }, 0)
    }

    return this._studentWorksheet.wrongCount
  }

  get score() {
    if (this.group.length > 1) {
      return Math.floor(
        this.group.reduce((result, curr) => {
          return curr.studentWorksheet.score ? result + curr.studentWorksheet.score : result
        }, 0) / this.group.length,
      )
    }

    return this._studentWorksheet.score
  }

  get status() {
    if (this.group.length > 1) {
      return this.group.every((item) => item.studentWorksheet.status === 'COMPLETE')
        ? 'COMPLETE'
        : this.group.every((item) => item.studentWorksheet.status === 'INCOMPLETE')
          ? 'INCOMPLETE'
          : 'PROGRESS'
    }

    return this._studentWorksheet.status
  }

  get selfLearningColl() {
    return this._selfLearningColl
  }

  get hasSelfLearningWorksheet() {
    return this._selfLearningColl.hasLevelUp || this._selfLearningColl.hasWrong
  }
  get type() {
    return this._worksheet.type
  }

  get isSelfLearningWorksheet() {
    try {
      AssignedStudentWorksheetHomeworksSelfLearnings.assertSelfLearningWorksheet(this._worksheet)
      return true
    } catch (e) {
      return false
    }
  }

  get originAssignedStudentWorksheet() {
    return this._originAssignedStudentWorksheet
  }

  get isParentWorksheet() {
    return !this.isSelfLearningWorksheet
  }

  static assertSelfLearningWorksheet(
    worksheet: Worksheet,
  ): asserts worksheet is SelfLearningWorksheet {
    if (worksheet.type !== 'SELF_LEARNING') {
      throw Error('자기주도학습지가 아닙니다.')
    }
  }
}
